<template>
  <div class="wrapper">
    <div class="text-center">
      <p class="text-h4 mt-6 mb-4 primary-color">Data quality</p>
    </div>

    <v-progress-circular v-if="loading" indeterminate class="d-block mx-auto"></v-progress-circular>

    <template v-else>
      <v-tabs v-model="activeTab" background-color="transparent" color="#047ec3" class="mb-4">
        <v-tab>Skills (F5)</v-tab>
        <v-tab>Course families (F6)</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="dq-tabs-items">
        <v-tab-item class="dq-tab-item">
          <v-card class="card-form mb-6">
            <v-card-title class="headline background-gradient">
              <span class="headline ml-3">Actions</span>
              <v-spacer></v-spacer>
              <v-btn
                class="button-gradient-half mr-2"
                depressed
                :loading="applying"
                :disabled="applying || approvedUnapplied === 0"
                @click="applyApprovedSkills"
              >
                Apply approved ({{ approvedUnapplied }})
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4 pl-8 pr-8">
              <p class="text-body-2 mb-0">
                Confirm only records a decision. Apply rewrites link tables for approved rows that are not yet applied.
                Take a database backup before applying.
              </p>
            </v-card-text>
          </v-card>

          <v-alert v-if="notice && activeTab === 0" :type="noticeType" text class="mb-4">
            {{ notice }}
          </v-alert>

          <v-card class="card-form mb-8">
            <v-card-title class="headline background-gradient dq-card-header">
              <span class="headline ml-3">Skills deduplication</span>
              <v-spacer></v-spacer>
              <div class="dq-status-toggles">
                <button
                  v-for="status in statusToggleOptions"
                  :key="'s-chip-' + status"
                  type="button"
                  class="dq-status-chip"
                  :class="{ 'dq-status-chip--active': filters.status === status }"
                  @click="toggleStatusFilter(status)"
                >
                  {{ status }}: {{ counterStatus(status) }}
                </button>
              </div>
            </v-card-title>
            <v-card-text class="pt-4 px-4">
              <v-simple-table class="dq-table">
                <thead>
                  <tr>
                    <th>Duplicate skill</th>
                    <th>Canonical skill</th>
                    <th>Similarity</th>
                    <th>Status</th>
                    <th>Applied</th>
                    <th>Reviewed by</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="items.length === 0">
                    <td colspan="7" class="text-center py-6">No rows for these filters.</td>
                  </tr>
                  <tr v-for="item in items" :key="'s-' + item.skill_id">
                    <td class="text-cell">
                      <div class="font-weight-medium">{{ item.skill_name || '—' }}</div>
                      <div class="text-caption uri-cell">{{ shortUri(item.skill_uri) }}</div>
                    </td>
                    <td class="text-cell">
                      <div class="font-weight-medium">{{ item.canonical_skill_name || '—' }}</div>
                      <div class="text-caption uri-cell">{{ shortUri(item.canonical_skill_uri) }}</div>
                    </td>
                    <td>{{ formatSimilarity(item.similarity) }}</td>
                    <td>{{ item.status }}</td>
                    <td>{{ formatApplied(item) }}</td>
                    <td>{{ item.reviewed_by || '—' }}</td>
                    <td class="text-no-wrap">
                      <template v-if="canActSkill(item)">
                        <v-btn
                          x-small
                          class="button-gradient-half mr-1 mb-1"
                          depressed
                          :disabled="busyId === item.skill_id"
                          @click="confirmSkill(item)"
                        >
                          Confirm
                        </v-btn>
                        <v-btn
                          x-small
                          outlined
                          color="#047ec3"
                          class="mb-1"
                          :disabled="busyId === item.skill_id"
                          @click="rejectSkill(item)"
                        >
                          Reject
                        </v-btn>
                      </template>
                      <v-btn
                        v-else-if="canUndoSkill(item)"
                        x-small
                        outlined
                        color="#047ec3"
                        class="mb-1"
                        :disabled="busyId === item.skill_id"
                        @click="undoSkill(item)"
                      >
                        Undo
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="d-flex align-center justify-space-between mt-4 px-2">
                <v-btn outlined color="#047ec3" :disabled="!canPrev || loadingPage" @click="prevPage">
                  Previous
                </v-btn>
                <span class="text-body-2">Page {{ page }} · {{ total }} total</span>
                <v-btn outlined color="#047ec3" :disabled="!canNext || loadingPage" @click="nextPage">
                  Next
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item class="dq-tab-item">
          <v-card class="card-form mb-6">
            <v-card-title class="headline background-gradient">
              <span class="headline ml-3">Actions</span>
              <v-spacer></v-spacer>
              <v-btn
                class="button-gradient-half mr-2"
                depressed
                :loading="applying"
                :disabled="applying || approvedUnapplied === 0"
                @click="applyApprovedFamilies"
              >
                Apply approved ({{ approvedUnapplied }})
              </v-btn>
              <v-btn
                class="button-gradient-half mr-2"
                depressed
                :loading="running"
                :disabled="running"
                @click="runF6Pass"
              >
                Run F6 pass
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4 pl-8 pr-8">
              <p class="text-body-2 mb-2">
                Confirm only records a decision. Apply updates courses.category_id for approved rows that are not yet applied.
                Take a database backup before applying.
              </p>
              <v-alert v-if="notice && activeTab === 1" :type="noticeType" text class="mb-0">
                {{ notice }}
              </v-alert>
            </v-card-text>
          </v-card>

          <v-card class="card-form mb-8">
            <v-card-title class="headline background-gradient dq-card-header">
              <span class="headline ml-3">Course families</span>
              <v-spacer></v-spacer>
              <div class="dq-status-toggles">
                <button
                  v-for="status in statusToggleOptions"
                  :key="'f-chip-' + status"
                  type="button"
                  class="dq-status-chip"
                  :class="{ 'dq-status-chip--active': filters.status === status }"
                  @click="toggleStatusFilter(status)"
                >
                  {{ status }}: {{ counterStatus(status) }}
                </button>
              </div>
            </v-card-title>
            <v-card-text class="pt-4 px-4">
              <v-simple-table class="dq-table">
                <thead>
                  <tr>
                    <th>Course title</th>
                    <th>Family</th>
                    <th>Confidence</th>
                    <th>Model</th>
                    <th>Status</th>
                    <th>Applied</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="items.length === 0">
                    <td colspan="7" class="text-center py-6">No rows for these filters.</td>
                  </tr>
                  <tr v-for="item in items" :key="'f-' + item.course_id">
                    <td class="text-cell">{{ item.course_title || '—' }}</td>
                    <td class="family-cell">
                      <v-textarea
                        v-if="canActFamily(item)"
                        v-model="item.family"
                        rows="2"
                        auto-grow
                        outlined
                        dense
                        hide-details
                      ></v-textarea>
                      <span v-else>{{ item.family || '—' }}</span>
                    </td>
                    <td>{{ formatConfidence(item.confidence) }}</td>
                    <td>{{ item.model || '—' }}</td>
                    <td>{{ item.status }}</td>
                    <td>{{ formatApplied(item) }}</td>
                    <td class="text-no-wrap">
                      <template v-if="canActFamily(item)">
                        <v-btn
                          x-small
                          class="button-gradient-half mr-1 mb-1"
                          depressed
                          :disabled="busyId === item.course_id"
                          @click="confirmFamily(item)"
                        >
                          Confirm
                        </v-btn>
                        <v-btn
                          x-small
                          outlined
                          color="#047ec3"
                          class="mb-1"
                          :disabled="busyId === item.course_id"
                          @click="rejectFamily(item)"
                        >
                          Reject
                        </v-btn>
                      </template>
                      <v-btn
                        v-else-if="canUndoFamily(item)"
                        x-small
                        outlined
                        color="#047ec3"
                        class="mb-1"
                        :disabled="busyId === item.course_id"
                        @click="undoFamily(item)"
                      >
                        Undo
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="d-flex align-center justify-space-between mt-4 px-2">
                <v-btn outlined color="#047ec3" :disabled="!canPrev || loadingPage" @click="prevPage">
                  Previous
                </v-btn>
                <span class="text-body-2">Page {{ page }} · {{ total }} total</span>
                <v-btn outlined color="#047ec3" :disabled="!canNext || loadingPage" @click="nextPage">
                  Next
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BackofficeDataQuality',
  data: () => ({
    activeTab: 0,
    loading: true,
    loadingPage: false,
    running: false,
    applying: false,
    busyId: null,
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
    f6RunLimit: 50,
    applyLimit: 50,
    counters: {
      byStatus: { pending: 0, approved: 0, rejected: 0 },
      approvedUnapplied: 0
    },
    filters: {
      status: 'pending'
    },
    statusToggleOptions: ['pending', 'approved', 'rejected'],
    notice: '',
    noticeType: 'success'
  }),
  computed: {
    page() {
      return Math.floor(this.offset / this.limit) + 1
    },
    canPrev() {
      return this.offset > 0
    },
    canNext() {
      return this.offset + this.limit < this.total
    },
    approvedUnapplied() {
      return this.counters && this.counters.approvedUnapplied != null
        ? this.counters.approvedUnapplied
        : 0
    },
    listPath() {
      return this.activeTab === 1
        ? 'backoffice/data-quality/families'
        : 'backoffice/data-quality/skills'
    }
  },
  watch: {
    activeTab() {
      this.offset = 0
      this.notice = ''
      this.load(false)
    }
  },
  async mounted() {
    await this.load(true)
  },
  methods: {
    counterStatus(key) {
      const byStatus = this.counters && this.counters.byStatus
      return byStatus && byStatus[key] != null ? byStatus[key] : 0
    },
    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? null : status
      this.offset = 0
      this.load(false)
    },
    shortUri(uri) {
      if (!uri) return '—'
      const text = String(uri)
      const marker = '/esco/'
      const idx = text.indexOf(marker)
      return idx >= 0 ? text.slice(idx + 1) : text
    },
    formatSimilarity(value) {
      if (value == null || value === '') return '—'
      const num = Number(value)
      if (Number.isNaN(num)) return String(value)
      return (num * 100).toFixed(1) + '%'
    },
    formatConfidence(value) {
      if (value == null || value === '') return '—'
      const num = Number(value)
      if (Number.isNaN(num)) return String(value)
      if (num <= 1 && num >= 0) return (num * 100).toFixed(1) + '%'
      return String(value)
    },
    formatApplied(item) {
      if (!item) return '—'
      if (item.status !== 'approved') return '—'
      return item.applied_at ? 'yes' : 'no'
    },
    canActSkill(item) {
      return item.status === 'pending'
    },
    canUndoSkill(item) {
      if (!item) return false
      if (item.status === 'approved') return !item.applied_at
      if (item.status === 'rejected') return true
      return false
    },
    canActFamily(item) {
      return item.status === 'pending'
    },
    canUndoFamily(item) {
      return this.canUndoSkill(item)
    },
    async api(path, method, body) {
      const options = {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Auth-Token': this.$cookie.get('jwt_token')
        }
      }
      if (method === 'POST') options.body = JSON.stringify(body || {})
      let response = await fetch(process.env.VUE_APP_API_ENDPOINT + path, options)
      response = await response.json()
      if (!response.success) throw new Error(response.message || 'Error')
      return response.result
    },
    buildQuery() {
      const params = new URLSearchParams()
      params.set('limit', String(this.limit))
      params.set('offset', String(this.offset))
      if (this.filters.status) {
        params.set('status', this.filters.status)
      }
      return params.toString()
    },
    prevPage() {
      this.offset = Math.max(0, this.offset - this.limit)
      this.load(false)
    },
    nextPage() {
      this.offset = this.offset + this.limit
      this.load(false)
    },
    handleAuthError(error) {
      if (error.message && error.message.includes('Authentication failed')) {
        this.$router.push('/auth-error')
        return true
      }
      return false
    },
    async load(initial) {
      if (initial) this.loading = true
      else this.loadingPage = true
      this.notice = ''
      try {
        const result = await this.api(this.listPath + '?' + this.buildQuery())
        this.items = (result.items || []).map((item) => Object.assign({}, item))
        this.total = result.total || 0
        this.limit = result.limit || this.limit
        this.offset = result.offset != null ? result.offset : this.offset
        if (result.counters) {
          this.counters = {
            byStatus: Object.assign(
              { pending: 0, approved: 0, rejected: 0 },
              result.counters.byStatus || {}
            ),
            approvedUnapplied:
              result.counters.approvedUnapplied != null ? result.counters.approvedUnapplied : 0
          }
        }
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.loading = false
        this.loadingPage = false
      }
    },
    async applyApprovedSkills() {
      if (!this.approvedUnapplied) return
      const ok = window.confirm(
        'Apply will rewrite link tables for ' +
          this.approvedUnapplied +
          ' approved skill mapping(s). Take a DB backup first. Continue?'
      )
      if (!ok) return
      this.applying = true
      this.notice = ''
      try {
        const result = await this.api('backoffice/data-quality/skills/apply', 'POST', {
          limit: this.applyLimit
        })
        const applied = result.applied || 0
        const remaining =
          result.counters && result.counters.approvedUnapplied != null
            ? result.counters.approvedUnapplied
            : 0
        await this.load(false)
        this.noticeType = 'success'
        this.notice =
          'Applied ' + applied + ' approved skill mapping(s). Remaining unapplied: ' + remaining + '.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.applying = false
      }
    },
    async applyApprovedFamilies() {
      if (!this.approvedUnapplied) return
      const ok = window.confirm(
        'Apply will update courses.category_id for ' +
          this.approvedUnapplied +
          ' approved family suggestion(s). Take a DB backup first. Continue?'
      )
      if (!ok) return
      this.applying = true
      this.notice = ''
      try {
        const result = await this.api('backoffice/data-quality/families/apply', 'POST', {
          limit: this.applyLimit
        })
        const applied = result.applied || 0
        const remaining =
          result.counters && result.counters.approvedUnapplied != null
            ? result.counters.approvedUnapplied
            : 0
        await this.load(false)
        this.noticeType = 'success'
        this.notice =
          'Applied ' +
          applied +
          ' approved family suggestion(s). Remaining unapplied: ' +
          remaining +
          '.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.applying = false
      }
    },
    async runF6Pass() {
      this.running = true
      this.notice = ''
      try {
        const result = await this.api('backoffice/data-quality/families/run', 'POST', {
          limit: this.f6RunLimit
        })
        this.noticeType = 'success'
        const processed =
          result && (result.processed != null ? result.processed : result.enqueued != null ? result.enqueued : null)
        this.notice =
          processed != null
            ? 'F6 pass finished: ' + processed + ' course(s) processed.'
            : 'F6 pass finished.'
        await this.load(false)
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.running = false
      }
    },
    async confirmSkill(item) {
      this.busyId = item.skill_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/skills/' + item.skill_id + '/confirm', 'POST')
        this.noticeType = 'success'
        this.notice = 'Skill duplicate confirmed.'
        await this.load(false)
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async unconfirmSkill(item) {
      this.busyId = item.skill_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/skills/' + item.skill_id + '/unconfirm', 'POST')
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Approval undone; mapping is pending again.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async unrejectSkill(item) {
      this.busyId = item.skill_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/skills/' + item.skill_id + '/unreject', 'POST')
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Rejection undone; mapping is pending again.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async undoSkill(item) {
      if (item.status === 'approved') return this.unconfirmSkill(item)
      if (item.status === 'rejected') return this.unrejectSkill(item)
    },
    async rejectSkill(item) {
      this.busyId = item.skill_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/skills/' + item.skill_id + '/reject', 'POST')
        this.noticeType = 'success'
        this.notice = 'Skill duplicate rejected.'
        await this.load(false)
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async confirmFamily(item) {
      this.busyId = item.course_id
      this.notice = ''
      try {
        const body = {}
        if (item.family != null && item.family !== '') {
          body.family = item.family
        }
        await this.api('backoffice/data-quality/families/' + item.course_id + '/confirm', 'POST', body)
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Course family confirmed.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async unconfirmFamily(item) {
      this.busyId = item.course_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/families/' + item.course_id + '/unconfirm', 'POST')
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Approval undone; suggestion is pending again.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async rejectFamily(item) {
      this.busyId = item.course_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/families/' + item.course_id + '/reject', 'POST')
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Course family rejected.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async unrejectFamily(item) {
      this.busyId = item.course_id
      this.notice = ''
      try {
        await this.api('backoffice/data-quality/families/' + item.course_id + '/unreject', 'POST')
        await this.load(false)
        this.noticeType = 'success'
        this.notice = 'Rejection undone; suggestion is pending again.'
      } catch (error) {
        if (this.handleAuthError(error)) return
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async undoFamily(item) {
      if (item.status === 'approved') return this.unconfirmFamily(item)
      if (item.status === 'rejected') return this.unrejectFamily(item)
    }
  }
}
</script>

<style scoped>
.wrapper {
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 40px;
}
.dq-tabs-items,
.dq-tabs-items >>> .v-window__container,
.dq-tab-item {
  overflow: visible !important;
}
.dq-tab-item {
  padding: 4px 8px 16px;
}
.card-form {
  box-shadow: 0 0 20px #eee !important;
}
.primary-color {
  color: #0096c7;
}
.background-gradient {
  color: white;
  box-shadow: 0 0 20px #eee;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 200%);
}
.button-gradient-half {
  color: white;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 100%);
}
.uri-cell,
.text-cell {
  max-width: 220px;
  white-space: normal;
  word-break: break-word;
}
.family-cell {
  min-width: 180px;
  max-width: 280px;
}
.dq-table td,
.dq-table th {
  vertical-align: top;
}
.dq-card-header {
  flex-wrap: wrap;
  row-gap: 8px;
}
.dq-status-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-right: 8px;
}
.dq-status-chip {
  border: 1px solid rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.dq-status-chip:hover {
  background: rgba(255, 255, 255, 0.22);
}
.dq-status-chip--active {
  background: #fff;
  color: #047ec3;
  border-color: #fff;
  font-weight: 600;
}
</style>
